import { collection, doc, runTransaction, serverTimestamp, getDoc } from "firebase/firestore"
import { db } from "./firebase"
import AuditService, { AUDIT_ACTIONS, AUDIT_MODULES } from "./audit_service"
import XpService, { XP_ACTION_TYPES } from "./xp_service"
import { addMonths, addYears, addDays } from "date-fns"
import { saleToFirestore, saleFromFirestore } from "../models/sale"
import { toISODate } from "../utils/date"
import { COLLECTIONS } from "../services/collections";
import CashierShiftService from "./cashier_shift_service";

/**
 * SalesService
 * Gerencia o processamento de vendas, criação de memberships e lançamentos financeiros.
 */
const SalesService = {
    /**
     * Busca uma venda por ID.
     */
    getById: async (idTenant, idSale) => {
        if (!idTenant || !idSale) return null;
        try {
            const docRef = doc(db, "tenants", idTenant, COLLECTIONS.SALES, idSale);
            const docSnap = await getDoc(docRef);
            if (docSnap.exists()) {
                return saleFromFirestore(docSnap);
            }
            return null;
        } catch (error) {
            console.error("Erro ao buscar venda:", error);
            throw error;
        }
    },

    /**
     * Processa uma venda completa de forma atômica usando transações.
     */
    processSale: async (idTenant, actor, payload) => {
        if (!idTenant) throw new Error("ID do Tenant é obrigatório.")

        try {
            const actorUid = actor?.uid || null;
            const canManageAllCashier = CashierShiftService.canManageAllCashier(actor);

            if (!canManageAllCashier && payload.idEmployee !== actorUid) {
                throw new Error("A venda deve ser registrada pelo colaborador autenticado.");
            }

            if (!payload.idCashierShift) {
                throw new Error("Um caixa aberto do colaborador é obrigatório para registrar a venda.");
            }

            const shift = await CashierShiftService.getById(idTenant, payload.idCashierShift);
            if (!shift) {
                throw new Error("Caixa selecionado não encontrado.");
            }

            if (shift.status !== "OPEN") {
                throw new Error("Não é possível registrar venda em um caixa fechado.");
            }

            if (!canManageAllCashier && shift.operatorId !== actorUid) {
                throw new Error("Você não pode registrar venda em um caixa que pertence a outro colaborador.");
            }

            // Converte a Payload da requisição (Que deve vir semelhante a saleDefaults) pro Formato Firestore
            const tenantInfo = { id: idTenant };
            const authUser = { uid: actorUid, name: payload.employeeName };
            const saleData = saleToFirestore(payload, tenantInfo, authUser);

            // Usamos uma transação para garantir que se um passo falhar, nada seja salvo.
            // REGRA Firestore: todas as leituras (get) ANTES de todas as escritas (set/update).
            const result = await runTransaction(db, async (transaction) => {

                // ═══════════════════════════════════════
                // FASE 1: LEITURAS (obrigatoriamente primeiro)
                // ═══════════════════════════════════════
                let memberSnap = null;
                let memberRef = null;

                if (payload.idMember) {
                    memberRef = doc(db, "tenants", idTenant, COLLECTIONS.MEMBERS, payload.idMember);
                    memberSnap = await transaction.get(memberRef);
                }

                // 2. Leitura do Contador Sequencial de Vendas
                const counterRef = doc(db, "tenants", idTenant, COLLECTIONS.COUNTERS, "sales");
                const counterSnap = await transaction.get(counterRef);

                // 3. Leitura do Employee (vendedor) se necessário
                let employeeData = null;
                if (saleData.idEmployee && (!saleData.employeeEmail || !saleData.employeePhone || !saleData.employeePhotoUrl)) {
                    const empRef = doc(db, "tenants", idTenant, "employees", saleData.idEmployee);
                    const empSnap = await transaction.get(empRef);
                    if (empSnap.exists()) {
                        employeeData = empSnap.data();
                    }
                }

                // 4. Leitura do Consultant se necessário
                let consultantData = null;
                // Pré-resolver idConsultant do membro (para poder ler o consultant na fase de leituras)
                let resolvedConsultantId = saleData.idConsultant;
                if (!resolvedConsultantId && memberSnap && memberSnap.exists()) {
                    const mData = memberSnap.data();
                    resolvedConsultantId = mData.consultant?.uid || mData.consultant?.id || null;
                }
                if (resolvedConsultantId && (!saleData.consultantEmail || !saleData.consultantPhone || !saleData.consultantPhotoUrl)) {
                    const consRef = doc(db, "tenants", idTenant, "employees", resolvedConsultantId);
                    const consSnap = await transaction.get(consRef);
                    if (consSnap.exists()) {
                        consultantData = consSnap.data();
                    }
                }

                // ═══════════════════════════════════════
                // FASE 2: ESCRITAS (após todas as leituras)
                // ═══════════════════════════════════════

                // Incrementar contador de vendas
                let saleSequence = 1000;
                if (counterSnap.exists() && typeof counterSnap.data().lastNumber === 'number') {
                    saleSequence = counterSnap.data().lastNumber + 1;
                }
                const formattedSaleNumber = String(saleSequence).padStart(5, '0');
                transaction.set(counterRef, { lastNumber: saleSequence }, { merge: true });

                // A. Enriquecer dados da venda com leituras já feitas
                if (memberSnap && memberSnap.exists()) {
                    const mData = memberSnap.data();
                    if (!saleData.idConsultant && (mData.consultant?.uid || mData.consultant?.id)) {
                        saleData.idConsultant = mData.consultant.uid || mData.consultant.id;
                        saleData.consultantName = mData.consultant.name || "";
                        saleData.consultantEmail = mData.consultant.email || "";
                    }
                    
                    saleData.memberEmail = saleData.memberEmail || mData.email || "";
                    saleData.memberPhone = saleData.memberPhone || mData.phone || "";
                    saleData.memberPhotoUrl = saleData.memberPhotoUrl || mData.photoUrl || "";

                    // Detecção automática de Renovação
                    if (mData.status === "active" || mData.status === "frozen") {
                        saleData.saleCategory = "RENEWAL";
                    }
                }

                // Enriquecer dados do Employee
                if (employeeData) {
                    saleData.employeeEmail = saleData.employeeEmail || employeeData.email || "";
                    saleData.employeePhone = saleData.employeePhone || employeeData.phone || "";
                    saleData.employeePhotoUrl = saleData.employeePhotoUrl || employeeData.photoUrl || "";
                    saleData.employeeName = saleData.employeeName || employeeData.fullName || employeeData.name || "";
                }

                // Enriquecer dados do Consultant
                if (consultantData) {
                    saleData.consultantEmail = saleData.consultantEmail || consultantData.email || "";
                    saleData.consultantPhone = saleData.consultantPhone || consultantData.phone || "";
                    saleData.consultantPhotoUrl = saleData.consultantPhotoUrl || consultantData.photoUrl || "";
                    saleData.consultantName = saleData.consultantName || consultantData.fullName || consultantData.name || "";
                }

                // Salvar a Venda
                const saleRef = doc(collection(db, "tenants", idTenant, COLLECTIONS.SALES));
                saleData.saleNumber = formattedSaleNumber;
                transaction.set(saleRef, saleData);

                // Variável para armazenar os IDs criados para poder vincular as matrículas posteriormente
                const createdMemberships = {};
                const saleItemsProcessingLog = [];

                // B. Criar registros de memberMembership para cada plano vendido
                for (const item of payload.items) {
                    if (item.type === 'membership') {
                        const memberMembershipRef = doc(collection(db, "tenants", idTenant, COLLECTIONS.MEMBER_MEMBERSHIPS));

                        // Se a venda tiver cartId, guardamos o reference ID criado
                        if (item.cartId) {
                            createdMemberships[item.cartId] = memberMembershipRef.id;
                        }

                        // Determinar a Duração e data final
                        const duration = parseInt(item.duration) || 1;
                        const durationType = item.durationType || "monthly";
                        const start = new Date(item.startDate);
                        let end = new Date(start);

                        // Cálculo de endDate seguro via date-fns
                        if (durationType === "monthly") {
                            end = addMonths(start, duration);
                        } else if (durationType === "yearly") {
                            end = addYears(start, duration);
                        } else if (durationType === "days") {
                            end = addDays(start, duration);
                        } else if (durationType === "classes") {
                            // Pacote de aulas: validade configurável (default 6 meses)
                            const validityMonths = parseInt(item.classPackageValidityMonths) || 6;
                            end = addMonths(start, validityMonths);
                        } else {
                            end = addMonths(start, 1);
                        }

                        saleItemsProcessingLog.push({
                            itemName: item.name || "N/A",
                            idMembership: item.idItem || "N/A",
                            inputStartDate: item.startDate instanceof Date ? item.startDate.toISOString() : String(item.startDate),
                            computedStartDate: toISODate(start),
                            computedEndDate: toISODate(end),
                            durationCalculated: duration,
                            durationTypeCalculated: durationType,
                            recordedAt: new Date().toISOString()
                        });

                        const actualMemberData = (memberSnap && memberSnap.exists()) ? memberSnap.data() : {};

                        const membershipPayload = {
                            idTenant,
                            idSale: saleRef.id,
                            idMember: payload.idMember, // Firestore document ID do membro
                            memberNumber: payload.memberNumber || "",       // ID sequencial do membro (ex: "0001")
                            memberName: payload.memberName,
                            memberEmail: actualMemberData.email || payload.memberEmail || "",
                            memberPhone: actualMemberData.phone || payload.memberPhone || "",
                            memberPhotoUrl: actualMemberData.photoUrl || "",

                            idMembership: item.idItem,
                            membershipName: item.name,
                            type: item.membershipType || "recurrent",
                            durationType,
                            duration,
                            enrollmentRequired: !!item.enrollmentRequired,
                            maxSessionsPerWeek: parseInt(item.maxSessionsPerWeek) || 0,
                            allowedDaysOfWeek: Array.isArray(item.allowedDaysOfWeek) ? item.allowedDaysOfWeek : [],

                            // Pacote de Aulas: saldo de créditos
                            totalClasses: durationType === 'classes' ? duration : 0,
                            remainingClasses: durationType === 'classes' ? duration : 0,

                            memberXp: parseInt(item.memberXp) || 0,
                            employeeXp: parseInt(item.employeeXp) || 0,

                            startDate: toISODate(start),
                            endDate: toISODate(end),

                            originalValue: item.unitPrice || 0,
                            discount: 0,
                            price: item.totalPrice || item.unitPrice || 0,
                            isScholarship: !!item.isScholarship,

                            status: "active",
                            
                            processingLog: saleItemsProcessingLog[saleItemsProcessingLog.length - 1],

                            allowsSuspension: !!item.allowsSuspension,
                            maxSuspensionDays: parseInt(item.maxSuspensionDays) || 0,
                            disponibleSuspensionDays: parseInt(item.maxSuspensionDays) || 0,
                            numberSuspensionTimes: 0,
                            suspensionFee: parseFloat(item.suspensionFee) || 0,
                            freezes: [],

                            cancellationFee: parseFloat(item.cancellationFee) || 0,
                            cancellationNoticeDays: parseInt(item.cancellationNoticeDays) || 0,
                            allowsCancellationByApp: !!item.allowsCancellationByApp,
                            cancelDate: null,
                            cancelReason: "",

                            idEmployee: payload.idEmployee || null,
                            employeeName: payload.employeeName || "",
                            employeeEmail: payload.employeeEmail || "",
                            employeePhone: payload.employeePhone || "",
                            employeePhotoUrl: payload.employeePhotoUrl || "",

                            createdAt: serverTimestamp(),
                            updatedAt: serverTimestamp(),
                            createdBy: { uid: actorUid, name: payload.employeeName },
                            updatedBy: { uid: actorUid, name: payload.employeeName }
                        };

                        transaction.set(memberMembershipRef, membershipPayload);

                        // ── XP do Membro: creditar automaticamente ──
                        const memberXpAmount = parseInt(item.memberXp) || 0;
                        if (memberXpAmount > 0 && payload.idMember) {
                            XpService.addXpInTransaction(transaction, idTenant, {
                                idMember: payload.idMember,
                                xpAmount: memberXpAmount,
                                actionType: XP_ACTION_TYPES.MEMBERSHIP_PURCHASE,
                                sourceId: memberMembershipRef.id,
                                sourceName: item.name,
                                user: authUser
                            });
                        }
                    }
                }

                // C. Se vendeu um plano, ativar o membro (status → "active")
                if (memberSnap && memberSnap.exists()) {
                    const currentStatus = memberSnap.data().status;
                    if (currentStatus !== "active") {
                        transaction.update(memberRef, {
                            status: "active",
                            updatedAt: serverTimestamp(),
                            updatedBy: { uid: actorUid, name: payload.employeeName }
                        });
                    }
                }

                return { saleId: saleRef.id, saleNumber: formattedSaleNumber, createdMemberships, saleItemsProcessingLog }
            })

            // 2. Auditoria (fora da transação para não bloquear)
            AuditService.log({
                idTenant,
                user: { uid: actorUid, displayName: payload.employeeName },
                module: AUDIT_MODULES.SALES,
                action: AUDIT_ACTIONS.CREATE,
                entityId: result.saleId,
                entityName: `Venda #${result.saleNumber}`,
                description: `Venda de R$ ${(payload.totalAmount || 0).toFixed(2)} para ${payload.memberName} — ${payload.items.length} item(s)`,
                details: {
                    // Resumo financeiro
                    totalAmount: payload.totalAmount || 0,
                    subtotal: payload.subtotal || 0,
                    discountTotal: payload.discountTotal || 0,
                    balance: payload.balance || 0,
                    date: payload.date || null,

                    // Membro
                    member: {
                        id: payload.idMember || null,
                        name: payload.memberName || "N/A",
                        number: payload.memberNumber || "N/A",
                    },

                    // Consultor / Funcionário
                    consultant: {
                        id: payload.idConsultant || payload.idEmployee || null,
                        name: payload.consultantName || payload.employeeName || "N/A",
                    },

                    // Caixa
                    idCashierShift: payload.idCashierShift || null,

                    // Itens vendidos (planos, produtos, serviços)
                    items: (payload.items || []).map(item => ({
                        name: item.name,
                        type: item.type,
                        unitPrice: item.unitPrice || 0,
                        totalPrice: item.totalPrice || item.unitPrice || 0,
                        quantity: item.quantity || 1,
                        // Dados do contrato (se for plano)
                        ...(item.type === 'membership' ? {
                            startDate: item.startDate instanceof Date ? item.startDate.toISOString().split('T')[0] : item.startDate,
                            duration: item.duration,
                            durationType: item.durationType,
                        } : {}),
                    })),

                    // Formas de pagamento
                    paymentMethods: (payload.paymentMethods || []).map(pm => ({
                        method: pm.methodLabel || pm.methodId,
                        value: pm.value || 0,
                        installments: pm.installments || 1,
                        brand: pm.brand || null,
                    })),

                    // Contratos criados (IDs dos memberMemberships)
                    createdMemberships: result.createdMemberships || {},

                    // Log de processamento detalhado (cálculo de datas)
                    processingTrace: result.saleItemsProcessingLog || [],
                }
            })

            return result;

        } catch (error) {
            console.error("Erro no SalesService.processSale:", error)
            throw error
        }
    }
}

export default SalesService
